import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import {
  CATEGORY_ORDER,
  buildTree,
  buildIndex,
  collectCheckedDragItems,
  TreeNodeItem,
  TextbookExplorer,
} from '../components/CurriculumTree';

const CURRENT_YEAR = new Date().getFullYear();
const SPECIALTY_ORDER = ['기체', '엔진', '전기', '전자', '판금'];

function groupExistingByCat(existing) {
  const groups = {};
  existing.forEach((a) => {
    const key = a.cat || '기타';
    if (!groups[key]) groups[key] = [];
    groups[key].push(a);
  });
  const orderedKeys = [
    ...CATEGORY_ORDER.filter((c) => groups[c]),
    ...Object.keys(groups).filter((k) => !CATEGORY_ORDER.includes(k)),
  ];
  return orderedKeys.map((cat) => ({ cat, items: groups[cat] }));
}

// 담당(전공) -> 학년 순으로 묶어서, 표에서 담당/학년 칸을 세로로 병합(rowSpan)해 보여주기 위한 행 목록을 만든다.
function buildGroupedTeacherRows(teachers) {
  const bySpecialty = new Map();
  teachers.forEach((t) => {
    const spec = t.specialty || '기타';
    if (!bySpecialty.has(spec)) bySpecialty.set(spec, new Map());
    const byGrade = bySpecialty.get(spec);
    const g = t.grade ?? 0;
    if (!byGrade.has(g)) byGrade.set(g, []);
    byGrade.get(g).push(t);
  });

  const specKeys = [
    ...SPECIALTY_ORDER.filter((s) => bySpecialty.has(s)),
    ...Array.from(bySpecialty.keys()).filter((s) => !SPECIALTY_ORDER.includes(s)),
  ];

  const rows = [];
  specKeys.forEach((spec) => {
    const byGrade = bySpecialty.get(spec);
    const gradeKeys = Array.from(byGrade.keys()).sort((a, b) => a - b);
    const specRowSpan = gradeKeys.reduce((sum, g) => sum + byGrade.get(g).length, 0);
    let specFirst = true;
    gradeKeys.forEach((g) => {
      const list = byGrade.get(g);
      let gradeFirst = true;
      list.forEach((t) => {
        rows.push({
          teacher: t,
          spec,
          grade: g,
          showSpec: specFirst,
          specRowSpan,
          showGrade: gradeFirst,
          gradeRowSpan: list.length,
        });
        specFirst = false;
        gradeFirst = false;
      });
    });
  });
  return rows;
}

export default function SupplementaryAssignment() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [typeclsOptions, setTypeclsOptions] = useState([]);
  const [typecls, setTypecls] = useState('보충');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set(['root']));
  const [tbExpanded, setTbExpanded] = useState(new Set());
  const [selectedId, setSelectedId] = useState('root');
  const [expandedParts, setExpandedParts] = useState(new Set());
  const [sectionsCache, setSectionsCache] = useState({});

  const [teachers, setTeachers] = useState([]);
  const [locationFilter, setLocationFilter] = useState('internal');
  const [existingAssignments, setExistingAssignments] = useState([]);
  const [checkedRows, setCheckedRows] = useState(new Set()); // "part:crsmatno" | "sect:crssectno"
  const [checkedTeachers, setCheckedTeachers] = useState(new Set());
  const [bulkThry, setBulkThry] = useState('');
  const [bulkPrac, setBulkPrac] = useState('');
  const [pushing, setPushing] = useState(false);
  const [pushMessage, setPushMessage] = useState('');

  useEffect(() => {
    api.get('/admin/course-plan-types').then((res) => {
      setTypeclsOptions(res.data);
      setTypecls((prev) => (res.data.includes(prev) ? prev : res.data[0] || prev));
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get('/admin/curriculum-tree', { params: { year } })
      .then((res) => setRows(res.data))
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => {
    setCheckedTeachers(new Set());
    api.get('/admin/teachers', { params: { location: locationFilter } }).then((res) => setTeachers(res.data));
  }, [locationFilter]);

  const loadExistingAssignments = React.useCallback(() => {
    api.get('/admin/assignments', { params: { year, typecls } }).then((res) => setExistingAssignments(res.data));
  }, [year, typecls]);

  useEffect(() => {
    loadExistingAssignments();
  }, [loadExistingAssignments]);

  const tree = useMemo(() => buildTree(rows), [rows]);
  const index = useMemo(() => {
    const idx = {};
    buildIndex(tree, null, idx);
    return idx;
  }, [tree]);

  useEffect(() => {
    if (!index[selectedId]) setSelectedId('root');
  }, [index, selectedId]);

  const allParts = useMemo(() => tree.children.flatMap((cat) => cat.allParts), [tree]);

  const toggleNode = (id) => {
    const entry = index[id];
    if (entry && entry.node.children && entry.node.children.length > 0) {
      const willOpen = !expanded.has(id);
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      setTbExpanded(willOpen ? new Set([id]) : new Set());
    } else if (entry) {
      setTbExpanded(entry.parent ? new Set([entry.parent.id]) : new Set());
    }
    setSelectedId(id);
  };

  const toggleTbNode = (id) => {
    setTbExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePart = (keyId, crsmatno) => {
    const key = `${keyId}::${crsmatno}`;
    setExpandedParts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        if (!sectionsCache[crsmatno]) {
          api.get(`/materials/${crsmatno}/sections`).then((res) => {
            setSectionsCache((sc) => ({ ...sc, [crsmatno]: res.data }));
          });
        }
      }
      return next;
    });
  };

  const toggleCheckRow = (rowKey) => {
    setCheckedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const toggleTeacherCheck = (teachid) => {
    setCheckedTeachers((prev) => {
      const next = new Set(prev);
      if (next.has(teachid)) next.delete(teachid);
      else next.add(teachid);
      return next;
    });
  };

  const allTeachersChecked = teachers.length > 0 && checkedTeachers.size === teachers.length;
  const someTeachersChecked = checkedTeachers.size > 0 && !allTeachersChecked;

  const toggleAllTeachers = () => {
    setCheckedTeachers(allTeachersChecked ? new Set() : new Set(teachers.map((t) => t.teachid)));
  };

  const handleDeleteExistingAssignment = async (asnid) => {
    await api.delete(`/admin/assignments/${asnid}`);
    loadExistingAssignments();
  };

  // 체크된 표준교재 PART/섹션을 챕터(crsmatno) 단위로 묶는다 (섹션만 체크됐어도 그 섹션이 속한 챕터 하나로 합침).
  const checkedChapters = useMemo(() => {
    const flat = collectCheckedDragItems(allParts, sectionsCache, checkedRows);
    const byChapter = new Map();
    flat.forEach((item) => {
      if (!byChapter.has(item.crsmatno)) {
        byChapter.set(item.crsmatno, { crsmatno: item.crsmatno, label: item.label, sections: [] });
      }
      if (item.crssectno) {
        byChapter.get(item.crsmatno).sections.push({ crssectno: item.crssectno, label: item.sectionLabel });
      }
    });
    return Array.from(byChapter.values());
  }, [allParts, sectionsCache, checkedRows]);

  const handleBulkPush = async () => {
    setPushMessage('');
    const teacherIds = Array.from(checkedTeachers);
    if (checkedChapters.length === 0) {
      setPushMessage('배정할 챕터를 표준교재에서 체크하세요.');
      return;
    }
    if (teacherIds.length === 0) {
      setPushMessage('배정받을 교사를 체크하세요.');
      return;
    }
    setPushing(true);
    try {
      for (const teachid of teacherIds) {
        for (const ch of checkedChapters) {
          await api.post('/admin/assignments', {
            asnyear: year,
            teachid,
            crsmatno: ch.crsmatno,
            typecls,
            thrytime: Number(bulkThry) || 0,
            practime: Number(bulkPrac) || 0,
            section_codes: ch.sections.map((s) => s.crssectno),
          });
        }
      }
      setCheckedRows(new Set());
      setBulkThry('');
      setBulkPrac('');
      setPushMessage('배정 완료');
      loadExistingAssignments();
    } catch (err) {
      setPushMessage(err.response?.data?.detail || '배정 실패');
    } finally {
      setPushing(false);
    }
  };

  const selected = index[selectedId]?.node || tree;
  const activeCrsmatnos = useMemo(() => {
    if (selected.type !== 'chapter') return null;
    return new Set(selected.relatedParts.map((p) => p.crsmatno));
  }, [selected]);

  const existingByTeacher = useMemo(() => {
    const map = {};
    existingAssignments.forEach((a) => {
      if (!map[a.teachid]) map[a.teachid] = [];
      map[a.teachid].push(a);
    });
    return map;
  }, [existingAssignments]);

  const teacherRows = useMemo(() => buildGroupedTeacherRows(teachers), [teachers]);

  return (
    <div className="page">
      <div className="page-title-row">
        <label>연도</label>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <h2>항공정비사 지정교과목 배정관리</h2>
      </div>

      <div className="card">
        {loading && <div className="form-message">불러오는 중...</div>}
        <div className="curr-layout">
          <div className="curr-left-col">
            <div className="curr-tree-label">항공안전법 지정교과목</div>
            <nav className="curr-tree">
              <ul className="curr-tree-list curr-tree-root">
                {tree.children.map((node) => (
                  <TreeNodeItem
                    key={node.id}
                    node={node}
                    expanded={expanded}
                    selectedId={selectedId}
                    onToggle={toggleNode}
                  />
                ))}
              </ul>
            </nav>

            <div className="curr-textbook-panel">
              <div className="curr-textbook-panel-title">
                <span className="curr-node-icon">📘</span> 표준교재
              </div>
              <TextbookExplorer
                categories={tree.children}
                tbExpanded={tbExpanded}
                onToggleTb={toggleTbNode}
                expandedParts={expandedParts}
                onTogglePart={togglePart}
                sectionsCache={sectionsCache}
                checkedRows={checkedRows}
                onToggleCheckRow={toggleCheckRow}
                activeCrsmatnos={activeCrsmatnos}
              />
            </div>
          </div>

          <div className="curr-bulk-panel">
            <div className="curr-bulk-field">
              <label>구분</label>
              <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                <option value="internal">원내교사</option>
                <option value="external">원외교사</option>
              </select>
            </div>
            <div className="curr-bulk-field">
              <label>수업구분</label>
              <select value={typecls} onChange={(e) => setTypecls(e.target.value)}>
                {typeclsOptions.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="curr-bulk-field">
              <label>학과</label>
              <input
                type="number"
                min="0"
                value={bulkThry}
                onChange={(e) => setBulkThry(e.target.value)}
              />
            </div>
            <div className="curr-bulk-field">
              <label>실습</label>
              <input
                type="number"
                min="0"
                value={bulkPrac}
                onChange={(e) => setBulkPrac(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="curr-bulk-arrow-btn"
              title="체크된 챕터를 체크된 교사들에게 한꺼번에 배정"
              disabled={pushing}
              onClick={handleBulkPush}
            >
              →
            </button>
            {pushMessage && <div className="curr-bulk-message">{pushMessage}</div>}
          </div>

          <div className="curr-main">
            <div className="table-scroll supp-assign-table-wrap">
              <table className="data-table curr-assign-table supp-assign-table">
                <thead>
                  <tr>
                    <th>담당</th>
                    <th>학년</th>
                    <th>이름</th>
                    <th className="supp-check-cell">
                      <input
                        type="checkbox"
                        title="전체 선택/해제"
                        checked={allTeachersChecked}
                        ref={(el) => {
                          if (el) el.indeterminate = someTeachersChecked;
                        }}
                        onChange={toggleAllTeachers}
                      />
                    </th>
                    <th>배정과목</th>
                    <th>수업구분</th>
                  </tr>
                </thead>
                <tbody>
                  {teacherRows.map((r) => {
                    const t = r.teacher;
                    const existing = existingByTeacher[t.teachid] || [];
                    const existingGroups = groupExistingByCat(existing);
                    return (
                      <tr key={t.teachid}>
                        {r.showSpec && (
                          <td className="supp-group-cell" rowSpan={r.specRowSpan}>{r.spec}</td>
                        )}
                        {r.showGrade && (
                          <td className="supp-group-cell" rowSpan={r.gradeRowSpan}>{r.grade ? `${r.grade}학년` : ''}</td>
                        )}
                        <td>{t.name}</td>
                        <td className="supp-check-cell">
                          <input
                            type="checkbox"
                            checked={checkedTeachers.has(t.teachid)}
                            onChange={() => toggleTeacherCheck(t.teachid)}
                          />
                        </td>
                        <td className="curr-existing-cell">
                          {existingGroups.length === 0 ? null : (
                            <div className="curr-basket-items">
                              {existingGroups.map(({ cat, items }) => (
                                <div key={cat} className="curr-existing-group">
                                  {items.map((a) => (
                                    <div key={a.asnid} className="curr-existing-entry">
                                      <span
                                        className="curr-existing-chip"
                                        title="클릭하면 이 배정이 삭제됩니다"
                                        onClick={() => {
                                          if (window.confirm(`"${a.chapter}" 배정을 삭제하시겠습니까?`)) {
                                            handleDeleteExistingAssignment(a.asnid);
                                          }
                                        }}
                                      >
                                        {a.chapter} ({a.thrytime}/{a.practime}) ✕
                                      </span>
                                      {a.sections.length > 0 && (
                                        <div className="curr-basket-sections">
                                          {a.sections.map((s) => (
                                            <span key={s.crssectno} className="curr-existing-section-tag">
                                              {s.label}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="supp-typecls-cell">{existing.length > 0 ? typecls : ''}</td>
                      </tr>
                    );
                  })}
                  {teacherRows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="empty-row">등록된 교사가 없습니다.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
